import {
    convertParametersToModel,
    LinkedService,
    Owned,
    ParameterConfig,
    ReferenceAdapter,
    WorkspaceBuildInLinkedService,
    linkedServiceReferenceToResource as SetlinkedServiceReferenceResource,
    Named,
    SharedModel,
    ConnectionKind,
    SnowflakeV2LinkedService,
    artifactsSupportDMTSCredential,
    artifactLinkedSerivceToKindMap,
    artifactLinkedSerivceType
} from 'shell-models-lib';
import { Model } from 'app/model/backendmodel';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { ActivityAsyncAdapter } from 'app/model/adapters/activities/activity.adapter';
import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { ScriptActivity } from 'app/model/pipeline/script.activity';
import { StagingSettings } from 'app/model/pipeline/staging-settings';
import { linkedServiceReferenceToResource } from 'app/shared/utils/linkedservice-parameter.util';
import { PlainScript } from 'app/model/pipeline/plain-script';
import { isLogicAppExpression } from 'app/shared/utils/expression.util';
import moment from 'moment-timezone';
import { isEmpty, merge } from 'lodash';
import { LinkedServiceAdapter } from '../linkedservices/linkedservice.adapter';
import { GlobalVariables } from 'app/services/globals';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import { ActivityConnectionContext, IScriptActivityContext } from 'app/interfaces/copilot.pipeline.action.types';
import { findLinkedServiceFromContext, setDbFromLs } from 'app/shared/utils/update-pipeline-skill.util';
import { type ModelService } from 'app/model/services/model.service';
import { LoggerService } from 'app/logger/logger.service';
import { generateUUID } from 'app/shared/utils/uuid.util';
import { type IFrameService } from 'app/services/iframe.service';
import { hasSnowV2ScriptParam } from 'app/shared/utils/activity-settings.util';
import { AzurePostgreSqlLinkedService } from 'app/model/linkedservice/azure-db-postgre.linkedservice';
import { toParameterClass, toParameterObject } from '../linkedservices/parameter-config.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(ScriptActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        type: '*',
        description: '*',
        dependsOn: '*',
        policy: '*',
        userProperties: '*',
        linkedService: '*',
        connectionSettings: '*',
        linkedServiceName: '*',
        typeProperties: {
            database: __IS_FABRIC_DPE__ ? '*' : undefined,
            scripts: '*',
            scriptBlockExecutionTimeout: '*',
            logSettings: {
                logDestination: '*',
                logLocationSettings: {
                    linkedServiceName: '*',
                    path: '*'
                }
            },
            connectionVersion: __IS_FABRIC_DPE__ ? '*' : undefined,
            treatDecimalAsString: '*',
            connectionProperties: __IS_FABRIC_DPE__ ? '*' : undefined,
        },
        externalReferences: '*'
    }
})
export class ScriptActivityAdapter extends ActivityAsyncAdapter<
    ScriptActivity,
    Model.SqlScriptActivity,
    IScriptActivityContext
> {
    public model(): IModel {
        return Metamodel.modelFor(ScriptActivity);
    }

    public override async postToModel(scriptActivity: ScriptActivity): Promise<void> {
        await super.postToModel(scriptActivity);
        /**
         * In `_toModel`, we can't identify the exact type of the linked service, so we need to set the connectionVersion
         * in the postToModel method.
         */
        if (__IS_FABRIC_DPE__) {
            if (scriptActivity.linkedService instanceof AzurePostgreSqlLinkedService) {
                scriptActivity.connectionVersion = '2.0';
            }
        }
    }

    public override async toFabricCopilotContext(activity: ScriptActivity): Promise<IScriptActivityContext> {
        if (activity) {
            const activityContext: IScriptActivityContext = await super.toFabricCopilotContext(activity);
            if (activity.linkedService) {
                const lsAdapter = this.context.asyncAdapterFor<
                    LinkedService,
                    Model.LinkedServiceResource,
                    ActivityConnectionContext
                >(Metamodel.modelOf(activity.linkedService));
                activityContext.Connection = await lsAdapter?.toFabricCopilotContext(activity?.linkedService);
                let database;
                if (
                    (database = ValueAdapter.toResource(activity, ScriptActivity.DATABASE)) &&
                    activityContext.Connection
                ) {
                    activityContext.Connection.Database = database;
                }
                activityContext.Scripts = (activity.plainScripts ?? []).map((script) => {
                    return {
                        Type: ValueAdapter.toResource(script, PlainScript.SCRIPT_QUERY_MODE, true),
                        Body: ValueAdapter.toResource(script, PlainScript.SCRIPT_TEXT, true)
                    };
                });
            }
            return activityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: IScriptActivityContext,
        model?: ScriptActivity
    ): Promise<ScriptActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            if (context.Connection) {
                const modelService = await getServiceInstance<ModelService>(this.context.contextData, 'ModelService');
                const iframeService = await getServiceInstance<IFrameService>(
                    this.context.contextData,
                    'IFrameService'
                );
                model.linkedService = (
                    await findLinkedServiceFromContext(context.Connection, {
                        modelService,
                        iframeService
                    })
                ).linkedService;
                if (model.linkedService) {
                    const lsAdapter = Metamodel.isModel(context.Connection.Type)
                        ? this.context.asyncAdapterFor<
                            LinkedService,
                            Model.LinkedServiceResource,
                            ActivityConnectionContext
                        >(Metamodel.modelByName(context.Connection.Type))
                        : undefined;
                    if (lsAdapter) {
                        await lsAdapter.fromFabricCopilotContext(context.Connection, model.linkedService);
                        setDbFromLs(model, model.linkedService);
                    }
                }
                ValueAdapter.toModel(model, ScriptActivity.DATABASE, context.Connection.Database);
                model.plainScripts = (context.Scripts ?? []).map((script) => {
                    const scriptModel = new PlainScript();
                    ValueAdapter.toModel(scriptModel, PlainScript.SCRIPT_QUERY_MODE, script.Type ?? 'Query');
                    ValueAdapter.toModel(scriptModel, PlainScript.SCRIPT_TEXT, script.Body);
                    return scriptModel;
                });
            }
        }
        return model;
    }

    protected async _toResource(activity: ScriptActivity): Promise<Model.SqlScriptActivity> {
        const modelScriptActivity: Model.SqlScriptActivity = super._getCommonActivityProperties(
            activity,
            'Script'
        ) as Model.SqlScriptActivity;
        const typeProperties: Model.SqlScriptActivityTypeProperties = {};
        let hasScriptParams: boolean = true;

        if (activity.linkedService) {
            if (activity.linkedService.isWorkspaceBuiltIn) {
                const linkedServiceAdapter = this.context.asyncAdapterFor(Metamodel.modelOf(activity.linkedService));
                const linkedServiceResource = await linkedServiceAdapter.toResource(activity.linkedService);
                if (activity.dmtsCredentialForArtifact) {
                    linkedServiceResource.properties.linkedServiceName = SetlinkedServiceReferenceResource(
                        activity.dmtsCredentialForArtifact,
                        this.context
                    );
                }
                this._setArtifactLinkedService(
                    modelScriptActivity,
                    linkedServiceResource,
                    activity.dmtsCredentialForArtifact != null
                );

                if (linkedServiceResource?.properties) {
                    Reflect.deleteProperty(linkedServiceResource, 'objectId');
                }
            } else {
                modelScriptActivity.linkedServiceName = this._toLinkedServiceReferenceModel(
                    activity.linkedService,
                    activity.linkedServiceParameters
                );

                if (
                    __IS_FABRIC_DPE__ &&
                    activity.linkedServiceType != null &&
                    Named.OBJECT_ID.templateValue(activity.linkedService)
                ) {
                    modelScriptActivity.linkedServiceName.referenceType = activity.linkedServiceType;
                }
            }

            // special hanlde for snowflake v2:
            // - ADF, check linkedService.version
            // - trident, check typeProperties.connectionVersion
            if (activity.linkedService instanceof SnowflakeV2LinkedService) {
                hasScriptParams = hasSnowV2ScriptParam(
                    __IS_FABRIC_DPE__ ? activity.connectionVersion : activity.linkedService.version
                );

                if (__IS_FABRIC_DPE__) {
                    typeProperties.connectionVersion = activity.connectionVersion;
                }
            }

            if (__IS_FABRIC_DPE__ && activity.linkedServiceProperties?.length > 0) {
                typeProperties.connectionProperties = {
                    ...toParameterObject(activity.linkedServiceProperties, false, undefined, true)
                }
            }
        }

        typeProperties.scripts = [];
        activity.plainScripts?.forEach((script) => {
            let parameters: Model.SqlScriptActivityTypePropertiesParameters[];
            if (!hasScriptParams) {
                parameters = undefined;
            } else if (script.scriptParams != null) {
                parameters = script.scriptParams.map((param) => {
                    return {
                        name: param.name,
                        type: param.type,
                        value: ValueAdapter.toResource(param, ParameterConfig.VALUE),
                        direction: param.direction,
                        size: param.size
                    };
                });
                if (parameters.length === 0) {
                    parameters = undefined; // avoid empty array
                }
            } else {
                parameters = ValueAdapter.toResource(script, PlainScript.SCRIPT_PARAMETERS);
            }
            typeProperties.scripts.push({
                parameters,
                type: ValueAdapter.toResource(script, PlainScript.SCRIPT_QUERY_MODE),
                text: ValueAdapter.toResource(script, PlainScript.SCRIPT_TEXT)
            });
        });

        try {
            typeProperties.scriptBlockExecutionTimeout =
                typeof activity.scriptBlockExecutionTimeout === 'number'
                    ? moment.duration(activity.scriptBlockExecutionTimeout, 'minutes').format('hh:mm:ss', {
                        trim: false
                    })
                    : ValueAdapter.toResource(activity, ScriptActivity.SCRIPT_BLOCK_EXECUTION_TIMEOUT);
        } catch {
            this._logError('Failed to convert scriptBlockExecutionTimeout into TimeSpan');
        }

        if (activity.enableLog) {
            const logDestionation = ValueAdapter.toResource(activity, ScriptActivity.LOG_DESTINATION);
            typeProperties.logSettings = {
                logDestination: logDestionation
            };
            if (logDestionation === 'ExternalStore' && activity.logStorageSettings) {
                typeProperties.logSettings.logLocationSettings = {
                    linkedServiceName: linkedServiceReferenceToResource(
                        activity.logStorageSettings.storageLinkedService,
                        this.context,
                        this.ensureLogger(),
                        activity.logStorageSettings.linkedServiceParameters
                    ),
                    path: ValueAdapter.toResource(activity.logStorageSettings, StagingSettings.PATH)
                };
            }
        }
        if (__IS_FABRIC_DPE__) {
            typeProperties.database = ValueAdapter.toResource(activity, ScriptActivity.DATABASE) || undefined;
        }
        if (activity.linkedService instanceof SnowflakeV2LinkedService &&
            (__IS_FABRIC_DPE__ ? activity.connectionVersion === '1.1' : activity.linkedService.version === '1.1')) {
            typeProperties.treatDecimalAsString = ValueAdapter.toResource(activity, ScriptActivity.TREAT_DECIMAL_AS_STRING);
        }

        modelScriptActivity.typeProperties = typeProperties;
        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            merge(modelScriptActivity, parsed);
        }

        return modelScriptActivity;
    }

    protected async _toModel(modelActivity: Model.SqlScriptActivity): Promise<ScriptActivity> {
        const activity: ScriptActivity = ScriptActivity.MODEL.create<ScriptActivity>(modelActivity.name);
        const logger = await getServiceInstance<LoggerService>(this.context.contextData, 'LoggerService');

        this._updatedCommonProperties(modelActivity, activity);

        const artifactLinkedServiceResource = this._getArtifactLinkedService(modelActivity);
        let linkedServiceModel: IModel;
        let linkedServiceName: Model.LinkedServiceReference = modelActivity.linkedServiceName;
        if (!linkedServiceName && __IS_FABRIC_DPE__ && !isEmpty(artifactLinkedServiceResource)) {
            const linkedServiceType = artifactLinkedServiceResource.properties?.type;

            // handle dmtsCredential
            if (linkedServiceType && artifactLinkedServiceResource.properties.linkedServiceName && __IS_FABRIC_DPE__) {
                const linkedServiceRegistryService = await getServiceInstance<LinkedServiceRegistryService>(
                    this.context.contextData,
                    'LinkedServiceRegistryService'
                );
                const linkedServiceMetadata =
                    linkedServiceRegistryService.getLinkedServiceConfiguration(linkedServiceType);
                if (linkedServiceMetadata?.dmtsCredentialConnectionType) {
                    if (
                        isLogicAppExpression(artifactLinkedServiceResource.properties.linkedServiceName.referenceName)
                    ) {
                        const model = Metamodel.modelByName(linkedServiceMetadata.dmtsCredentialConnectionType);
                        if (model) {
                            const templateLinkedService = Owned.create<LinkedService>(model, activity);
                            templateLinkedService.name = artifactLinkedServiceResource.name || generateUUID().replaceAll('-', '_');
                            ValueAdapter.toModel(
                                templateLinkedService,
                                Metamodel.modelByName(linkedServiceMetadata.dmtsCredentialConnectionType).attribute(
                                    'objectId'
                                ),
                                artifactLinkedServiceResource.properties.linkedServiceName.referenceName
                            );
                            templateLinkedService.parameterized = true;
                            activity.dmtsCredentialForArtifact = templateLinkedService;
                        }
                        logger.log('script-activity.adapter', 'artifact credential linkedService is parameterized', {
                            linkedServiceType
                        });
                    } else {
                        activity.dmtsCredentialForArtifact = ReferenceAdapter.toProxyModel(
                            Metamodel.modelByName(linkedServiceMetadata.dmtsCredentialConnectionType),
                            artifactLinkedServiceResource.properties.linkedServiceName,
                            this.context
                        );
                    }
                }
            }

            if (this._shouldGetArtifactDMTSConnectionFromSolutionTemplate()) {
                const dmtsCredentialKey = `${modelActivity.name}/Script`;
                const dmtsCredentialForArtifact = this.context.adapterConfig.artifactDMTSCredentials.get(dmtsCredentialKey);
                if (dmtsCredentialForArtifact != null) {
                    activity.dmtsCredentialForArtifact = dmtsCredentialForArtifact;
                }
            }

            linkedServiceModel = Metamodel.modelByName(linkedServiceType);
            const linkedServiceAdapter = this.context.asyncAdapterFor(
                linkedServiceModel
            ) as LinkedServiceAdapter<LinkedService>;
            // Handle workspace built-in linked service in dynamic content
            const lsModel: WorkspaceBuildInLinkedService = Owned.create<WorkspaceBuildInLinkedService>(
                linkedServiceModel,
                activity
            );
            await linkedServiceAdapter?.toModel(
                {
                    ...artifactLinkedServiceResource,
                    properties: {
                        ...artifactLinkedServiceResource.properties,
                        linkedServiceName: undefined
                    }
                },
                lsModel
            );
            if (lsModel?.parameterized || activity?.dmtsCredentialForArtifact?.parameterized) {
                activity.linkedService = lsModel;
                if (lsModel?.parameterized) {
                    logger.log('script-activity.adapter', 'linkedService is parameterized', { linkedServiceType });
                }
            } else {
                linkedServiceName = {
                    type: 'LinkedServiceReference',
                    referenceName: linkedServiceAdapter.getArtifactId(artifactLinkedServiceResource),
                    workspaceObjectId: linkedServiceAdapter.getWorkspaceObjectId(artifactLinkedServiceResource)
                };
            }
        }
        if (linkedServiceName != null) {
            if (
                __IS_FABRIC_DPE__ &&
                isLogicAppExpression((linkedServiceName as SharedModel.LinkedServiceReference).referenceName)
            ) {
                let linkedServiceType: string = linkedServiceName.referenceType;
                activity.linkedServiceType = linkedServiceType;
                if (!linkedServiceType) {
                    logger.log(
                        'script-activity.adapter',
                        'linkedService connection id is parameterized & cannot find related linkedService type',
                        { resourceName: modelActivity.name }
                    );

                    const linkedServiceRegistryService = await getServiceInstance<LinkedServiceRegistryService>(
                        this.context.contextData,
                        'LinkedServiceRegistryService'
                    );
                    linkedServiceType = linkedServiceRegistryService
                        .getAllLinkedServiceConfigurations()
                        .filter(
                            (metadata) =>
                                metadata.supportScriptActivity && !linkedServiceRegistryService.hidedByEc(metadata.type)
                        )[0]?.type;
                }

                if (linkedServiceType) {
                    const lsModel = Metamodel.modelByName(linkedServiceType);
                    const templateLinkedService = Owned.create<LinkedService>(lsModel, activity);
                    templateLinkedService.name = generateUUID().replaceAll('-', '_');
                    ValueAdapter.toModel(
                        templateLinkedService,
                        Metamodel.modelByName(linkedServiceType).attribute('objectId'),
                        (linkedServiceName as SharedModel.LinkedServiceReference).referenceName
                    );
                    templateLinkedService.tridentArtifactType =
                        templateLinkedService.tridentArtifactType ?? 'Connection';
                    const iframeService = await getServiceInstance<IFrameService>(
                        this.context.contextData,
                        'IFrameService'
                    );
                    templateLinkedService.tridentConnectionKind = (iframeService.currentCustomerProfile.linkedServiceToConnectionTypeMapping[linkedServiceType] || linkedServiceType) as ConnectionKind;
                    templateLinkedService.parameterized = true;
                    activity.linkedService = templateLinkedService;
                }

                logger.log('script-activity.adapter', 'linkedService connection id is parameterized', { linkedServiceType });
            } else {
                activity.linkedService = ReferenceAdapter.toProxyModel<LinkedService>(
                    linkedServiceModel ?? Metamodel.modelFor(LinkedService),
                    linkedServiceName,
                    this.context
                );
            }
            if (linkedServiceName.parameters) {
                convertParametersToModel(
                    activity,
                    !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                    activity.linkedService,
                    activity.linkedServiceParameters,
                    linkedServiceName.parameters
                );
            }
        }
        activity.plainScripts = [];
        modelActivity.typeProperties.scripts?.forEach((script) => {
            const plainScript = Owned.create<PlainScript>(PlainScript.MODEL, activity);
            ValueAdapter.toModel(plainScript, PlainScript.SCRIPT_QUERY_MODE, script.type);
            ValueAdapter.toModel(plainScript, PlainScript.SCRIPT_TEXT, script.text);
            if (script.parameters != null && Array.isArray(script.parameters)) {
                plainScript.scriptParams = [];
                script.parameters.forEach((parameter) => {
                    const param = Owned.create<ParameterConfig>(ParameterConfig.MODEL, activity);
                    param.type = parameter.type;
                    param.name = parameter.name;
                    ValueAdapter.toModel(param, ParameterConfig.VALUE, parameter.value);
                    param.direction = parameter.direction;
                    param.size = parameter.size;
                    plainScript.scriptParams.push(param);
                });
            } else {
                ValueAdapter.toModel(plainScript, PlainScript.SCRIPT_PARAMETERS, script.parameters);
            }
            activity.plainScripts.push(plainScript);
        });
        if (__IS_FABRIC_DPE__) {
            ValueAdapter.toModel(activity, ScriptActivity.DATABASE, modelActivity.typeProperties.database);
            activity.connectionVersion = modelActivity.typeProperties.connectionVersion;
            if (modelActivity.typeProperties.connectionProperties) {
                activity.linkedServiceProperties = toParameterClass(
                    modelActivity.typeProperties.connectionProperties,
                    activity
                ) as Array<ParameterConfig<string>>;
            }
        }
        activity.treatDecimalAsString = modelActivity.typeProperties.treatDecimalAsString;
        ValueAdapter.toModel(activity, ScriptActivity.TREAT_DECIMAL_AS_STRING, modelActivity.typeProperties.treatDecimalAsString);

        const scriptBlockTimeout = modelActivity.typeProperties.scriptBlockExecutionTimeout;
        try {
            if (scriptBlockTimeout == null) {
                activity.scriptBlockExecutionTimeout = undefined;
            } else {
                if (typeof scriptBlockTimeout === 'string' && !isLogicAppExpression(scriptBlockTimeout)) {
                    activity.scriptBlockExecutionTimeout = moment.duration(scriptBlockTimeout).asMinutes();
                } else {
                    ValueAdapter.toModel(activity, ScriptActivity.SCRIPT_BLOCK_EXECUTION_TIMEOUT, scriptBlockTimeout);
                }
            }
        } catch {
            this._logError('Failed to convert scriptBlockExecutionTimeout into number');
        }

        if (modelActivity.typeProperties.logSettings) {
            activity.enableLog = true;
            const logStorageSettings = modelActivity.typeProperties.logSettings;
            ValueAdapter.toModel(activity, ScriptActivity.LOG_DESTINATION, logStorageSettings.logDestination);
            if (activity.logDestination === 'ExternalStore') {
                const stagingSettings = logStorageSettings.logLocationSettings;
                activity.logStorageSettings.storageLinkedService = ReferenceAdapter.toProxyModel<LinkedService>(
                    Metamodel.modelFor(LinkedService),
                    stagingSettings.linkedServiceName,
                    this.context
                );
                if (stagingSettings.linkedServiceName && stagingSettings.linkedServiceName.parameters) {
                    convertParametersToModel(
                        activity.logStorageSettings,
                        !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                        activity.logStorageSettings.storageLinkedService,
                        activity.logStorageSettings.linkedServiceParameters,
                        stagingSettings.linkedServiceName.parameters
                    );
                }
                ValueAdapter.toModel(activity.logStorageSettings, StagingSettings.PATH, stagingSettings.path);
            }
        } else {
            activity.enableLog = false;
        }

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelActivity, ScriptActivity.MODEL));
        return activity;
    }

    private _getArtifactLinkedService(resource: Model.SqlScriptActivity): Model.LinkedServiceResource {
        if (
            !GlobalVariables.enableConnectionSettings &&
            !artifactsSupportDMTSCredential.includes(artifactLinkedSerivceToKindMap[resource.connectionSettings?.properties?.type as artifactLinkedSerivceType])
        ) {
            return resource.linkedService;
        }
        return resource.connectionSettings ?? resource.linkedService;
    }

    private _setArtifactLinkedService(
        resource: Model.SqlScriptActivity,
        linkedService: Model.LinkedServiceResource,
        withDmtsCredential: boolean = false
    ): void {
        resource[
            (
                GlobalVariables.enableConnectionSettings ||
                artifactsSupportDMTSCredential.includes(artifactLinkedSerivceToKindMap[linkedService?.properties?.type as artifactLinkedSerivceType])
            ) &&
                withDmtsCredential
                ? 'connectionSettings'
                : 'linkedService'
        ] = linkedService;
    }
}
